import React, { useCallback, useState } from 'react'
import { notifyOk, notifyError, makeGraphQLErrorNotification } from '../../../helpers/notification'
import { useBatchCreateStorageContainers } from '../../../graphql/mutation/storageContainer/batchCreateStorageContainers.gql'
import { CONTAINER_SIZES, CONTAINER_TYPES } from '../../../constants/storageContainer'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { Form, Button, DatePicker, InputNumber, SelectPicker, Modal } from 'rsuite'
import FormCheckbox from '@components/common/form/FormCheckbox'
import FormTextareaControl from '@components/common/form/FormTextareaControl'
import ContainerLabelPrinter from '../print/ContainerLabelPrinter'
import { StorageContainer } from '@@partner:types:app'
import {
  BatchCreateStorageContainerFormState,
  makeBatchCreateStorageContainerFormState,
  mapStateToApiVariable
} from './BatchCreateStorageContainerFormState'
import { BatchCreateStorageContainerValidationModel } from './validation/BatchCreateStorageContainerValidationModel'

interface BatchCreateStorageContainersFormProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const BatchCreateStorageContainersForm: React.FC<BatchCreateStorageContainersFormProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const { formState, setFormState, setFormRef } = useValidatableForm<BatchCreateStorageContainerFormState>(
    makeBatchCreateStorageContainerFormState
  )
  
  const [ batchCreateContainers, { loading } ] = useBatchCreateStorageContainers()
  const [ createdContainers, setCreatedContainers ] = useState<StorageContainer[]>([])
  const [ showPrint, setShowPrint ] = useState(false)

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState(state => ({ ...state, ...formData }))
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        batchCreateContainers({
          variables: { input: mapStateToApiVariable(formState) }
        })
          .then((result) => {
            if (result.data) {
              const containers = result.data.batchCreateStorageContainers
              notifyOk('Успех', `Успешно создано ${containers.length} контейнеров`)
              setCreatedContainers(containers)
              setShowPrint(true)
              onSuccess?.()
            }
          })
          .catch(makeGraphQLErrorNotification('Ошибка создания контейнеров'))
      } 
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, batchCreateContainers, onSuccess ]
  )

  const handleClosePrint = () => {
    setShowPrint(false)
    setCreatedContainers([])
    onClose()
  }

  return (
    <>
      <Modal open={open && !showPrint} onClose={onClose} size="md">
        <Modal.Header>
          <Modal.Title>Массовое создание контейнеров</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            formValue={formState}
            onChange={handleFormChange}
            onSubmit={handleFormSubmit}
            ref={setFormRef}
            model={BatchCreateStorageContainerValidationModel}
            fluid
          >
            <Form.Group>
              <Form.ControlLabel>Количество контейнеров *</Form.ControlLabel>
              <Form.Control
                name="count"
                accepter={InputNumber}
                min={1}
                max={100}
              />
              <Form.HelpText>От 1 до 100 контейнеров</Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Размер *</Form.ControlLabel>
              <SelectPicker
                value={formState.size}
                onChange={(value) => handleFormChange({ size: value })}
                data={CONTAINER_SIZES}
                valueKey="value"
                labelKey="label"
                searchable={false}
                cleanable={false}
                style={{ width: '100%' }}
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Тип *</Form.ControlLabel>
              <SelectPicker
                value={formState.type}
                onChange={(value) => handleFormChange({ type: value })}
                data={CONTAINER_TYPES}
                valueKey="value"
                labelKey="label"
                searchable={false}
                cleanable={false}
                style={{ width: '100%' }}
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Префикс кода</Form.ControlLabel>
              <Form.Control
                name="prefix"
                placeholder="SC"
              />
              <Form.HelpText>Будет добавлен к началу каждого кода</Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Описание</Form.ControlLabel>
              <FormTextareaControl
                name="description"
                rows={3}
                placeholder="Опциональное описание для всех контейнеров"
              />
            </Form.Group>
          
            <Form.Group>
              <Form.ControlLabel>Дата следующей санобработки</Form.ControlLabel>
              <Form.Control
                name="next_sanitize_at"
                accepter={DatePicker}
                placeholder="Выберите дату..."
                style={{ width: '100%' }}
                format="dd.MM.yyyy"
                container={() => document.body}
              />
              <Form.HelpText>
                Когда требуется санитарная обработка для всех контейнеров
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Интервал санобработки (дней)</Form.ControlLabel>
              <Form.Control
                name="sanitization_interval_days"
                accepter={InputNumber}
                placeholder="Введите количество дней..."
                min={1}
                style={{ width: '100%' }}
              />
              <Form.HelpText>
                Периодичность проведения санитарной обработки
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Активировать сразу после создания</Form.ControlLabel>
              <FormCheckbox.Control
                name="is_active"
                placeholder="Активировать сразу после создания"
              />
            </Form.Group>

            <Form.Group style={{ marginTop: 20 }}>
              <div className="flex justify-end space-x-3">
                <Button
                  appearance="subtle"
                  onClick={onClose}
                  disabled={loading}
                >
                  Отмена
                </Button>
                <Button
                  appearance="primary"
                  type="submit"
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? 'Создание...' : `Создать ${formState.count} контейнеров`}
                </Button>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal open={showPrint && createdContainers.length > 0} onClose={handleClosePrint} size="lg">
        <Modal.Header>
          <Modal.Title>Печать этикеток контейнеров</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ContainerLabelPrinter
            containers={createdContainers}
            onClose={handleClosePrint}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}
